import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  BookOpen,
  GraduationCap,
  Plus,
  Edit,
  Trash2,
  Star,
  Calendar,
  Search,
  Play,
  Pause,
  Eye,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { Course } from '../types'
import coursesService from '../services/coursesService'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import ConfirmationDialog from '../components/ConfirmationDialog'

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** List row: courses may use fixed start/end dates or recurring months + start day. */
function formatCourseScheduleSummary(course: Course): string {
  const sched = course.schedule
  if (!sched) return 'No schedule saved'
  if (sched.startDate && sched.endDate) {
    return `${new Date(sched.startDate).toLocaleDateString()} – ${new Date(sched.endDate).toLocaleDateString()}`
  }
  const months = sched.activeMonths
  const startDay = sched.startDay
  if (sched.dateRanges?.length) {
    return sched.dateRanges
      .map((r) => {
        const s = new Date(r.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        const e = new Date(r.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        return `${s} – ${e}`
      })
      .join('; ')
  }
  if (sched.monthStartDays?.length) {
    return [...sched.monthStartDays]
      .sort((a, b) => (a.year ?? 0) - (b.year ?? 0) || a.month - b.month)
      .map((e) => {
        const name = MONTH_SHORT[e.month - 1] || String(e.month)
        return e.year != null ? `${name} ${e.year} (day ${e.startDay})` : `${name} ${e.startDay}`
      })
      .join(', ')
  }
  if (months && months.length > 0) {
    const labels = [...months].sort((a, b) => a - b).map((m) => MONTH_SHORT[m - 1] || String(m))
    return startDay != null ? `Day ${startDay} of month · ${labels.join(', ')}` : labels.join(', ')
  }
  if (startDay != null) {
    return `Monthly · day ${startDay}`
  }
  return 'No fixed dates (edit course to add schedule)'
}

function formatDurationLabel(days: number): string {
  if (days === 1) return '1 day'
  return `${days} days`
}

const Courses: React.FC = () => {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<Course[]>([])
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCourses, setTotalCourses] = useState(0)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [courseToDelete, setCourseToDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const [courseToToggleStatus, setCourseToToggleStatus] = useState<Course | null>(null)
  const [showFeaturedDialog, setShowFeaturedDialog] = useState(false)
  const [courseToToggleFeatured, setCourseToToggleFeatured] = useState<Course | null>(null)
  const { } = useAuth()

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, filterType, filterStatus])

  const fetchCourses = useCallback(async () => {
    try {
      if (isInitialLoading) {
        setIsInitialLoading(true)
      } else {
        setIsTableLoading(true)
      }
      const params: {
        page: number
        limit: number
        sort: string
        order: 'desc'
        search?: string
        type?: string
        isActive?: boolean
      } = {
        page: currentPage,
        limit: 10,
        sort: 'createdAt',
        order: 'desc'
      }

      if (debouncedSearchTerm) params.search = debouncedSearchTerm
      if (filterType !== 'all') params.type = filterType
      if (filterStatus === 'active') params.isActive = true
      if (filterStatus === 'inactive') params.isActive = false

      const response = await coursesService.getCourses(params)
      
      console.log('Courses API Response:', response)
      
      if (response.success) {
        const coursesData = response.data?.courses || []
        console.log('Fetched courses:', coursesData.length, coursesData.map(c => ({ id: c._id, name: c.name, schoolId: c.schoolId })))
        setCourses(coursesData)
        setTotalPages(response.data?.pagination?.pages || 1)
        setTotalCourses(response.data?.pagination?.total || coursesData.length)
      } else {
        console.error('API response not successful:', response)
        toast.error('Failed to fetch courses')
      }
    } catch (error) {
      console.error('Error fetching courses:', error)
      toast.error('Failed to fetch courses')
    } finally {
      setIsInitialLoading(false)
      setIsTableLoading(false)
    }
  }, [currentPage, debouncedSearchTerm, filterType, filterStatus, isInitialLoading])

  useEffect(() => {
    fetchCourses()
  }, [fetchCourses])

  const handleToggleStatusClick = (course: Course) => {
    setCourseToToggleStatus(course)
    setShowStatusDialog(true)
  }

  const handleToggleStatusConfirm = async () => {
    if (!courseToToggleStatus) return
    try {
      const response = await coursesService.toggleCourseStatus(courseToToggleStatus._id)
      if (response.success) {
        setCourses(courses.map(course => 
          course._id === courseToToggleStatus._id 
            ? { ...course, isActive: !course.isActive }
            : course
        ))
        toast.success('Course status updated')
        setShowStatusDialog(false)
        setCourseToToggleStatus(null)
      }
    } catch (error: any) {
      console.error('Error toggling course status:', error)
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.response?.data?.details || error?.message || 'Failed to update course status'
      toast.error(msg)
    }
  }

  const handleToggleFeaturedClick = (course: Course) => {
    setCourseToToggleFeatured(course)
    setShowFeaturedDialog(true)
  }

  const handleToggleFeaturedConfirm = async () => {
    if (!courseToToggleFeatured) return
    try {
      const response = await coursesService.toggleCourseFeatured(courseToToggleFeatured._id)
      if (response.success) {
        setCourses(courses.map(course => 
          course._id === courseToToggleFeatured._id 
            ? { ...course, isFeatured: !course.isFeatured }
            : course
        ))
        toast.success('Course featured status updated')
        setShowFeaturedDialog(false)
        setCourseToToggleFeatured(null)
      }
    } catch (error: any) {
      console.error('Error toggling course featured:', error)
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.response?.data?.details || error?.message || 'Failed to update course featured status'
      toast.error(msg)
    }
  }

  const handleDeleteCourse = (courseId: string) => {
    setCourseToDelete(courseId)
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async () => {
    if (!courseToDelete) return
    
    try {
      setDeleting(true)
      const response = await coursesService.deleteCourse(courseToDelete)
      if (response.success) {
        toast.success('Course deleted successfully')
        fetchCourses()
        setShowDeleteDialog(false)
        setCourseToDelete(null)
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.response?.data?.details || error?.message || 'Failed to delete course'
      toast.error(msg)
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false)
    setCourseToDelete(null)
  }


  const displayedCourses = courses

  const getCourseTypeColor = (type: string) => {
    switch (type) {
      case 'TRAINING': return 'bg-primary-100 text-primary-800'
      case 'WORKSHOP': return 'bg-primary-200 text-primary-900'
      case 'RETREAT': return 'bg-primary-300 text-primary-900'
      case 'CLASS': return 'bg-primary-400 text-primary-900'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getDifficultyColor = (difficulty?: string) => {
    if (!difficulty) return 'bg-gray-100 text-gray-600'
    switch (difficulty) {
      case 'BEGINNER': return 'bg-green-100 text-green-800'
      case 'INTERMEDIATE': return 'bg-warning-100 text-warning-800'
      case 'ADVANCED': return 'bg-error-100 text-error-800'
      case 'ALL_LEVELS': return 'bg-primary-100 text-primary-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 shadow-2xl rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-primary-700/20 to-primary-800/20"></div>
        <div className="relative flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full border-2 border-white shadow-lg"></div>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">🎓 Course Management</h1>
              <p className="text-white/90 text-sm">Manage your yoga courses and programs</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/courses/create')}
            className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-300 border border-white/30 shadow-lg hover:shadow-xl hover:scale-105"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Course
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-primary-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Courses</p>
                  <p className="text-2xl font-bold text-gray-900">{courses.length}</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                    <Play className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Active Courses</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {courses.filter(c => c.isActive).length}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-xl flex items-center justify-center">
                    <Star className="h-6 w-6 text-yellow-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Featured Courses</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {courses.filter(c => c.isFeatured).length}
                  </p>
                </div>
              </div>
            </motion.div>

          </div>

      {/* Filters and Search */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.5 }}
        className="bg-white shadow-lg rounded-xl p-6 border border-gray-100"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full transition-all duration-200 bg-gray-50 focus:bg-white"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 focus:bg-white transition-all duration-200 appearance-none pr-8"
              >
                <option value="all">All Types</option>
                <option value="TRAINING">Training</option>
                <option value="RETREAT">Retreat</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 focus:bg-white transition-all duration-200 appearance-none pr-8"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Courses Table */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 relative">
        <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <GraduationCap className="h-5 w-5 text-primary-600" />
              <span>Courses ({totalCourses})</span>
            </h3>
          </div>
        </div>
        
        {displayedCourses.length === 0 ? (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mb-4">
              <GraduationCap className="h-12 w-12 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No courses found</h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              {debouncedSearchTerm || filterType !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your search or filters to find what you\'re looking for.'
                : 'Get started by creating your first course and begin your journey.'}
            </p>
            {!debouncedSearchTerm && filterType === 'all' && filterStatus === 'all' && (
              <button
                onClick={() => navigate('/courses/create')}
                className="inline-flex items-center px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Your First Course
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Course Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Pricing
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {displayedCourses.map((course, index) => (
                  <motion.tr 
                    key={course._id} 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-primary-100/30 transition-all duration-300 group"
                  >
                    <td className="px-6 py-6">
                      <div className="flex items-start space-x-4">
                        <div className="relative flex-shrink-0">
                          {course.images && course.images.length > 0 ? (
                            <img
                              className="h-16 w-16 rounded-xl object-cover shadow-md ring-2 ring-white"
                              src={course.images[0]}
                              alt={course.name}
                            />
                          ) : (
                            <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center shadow-md ring-2 ring-white">
                              <GraduationCap className="h-8 w-8 text-primary-600" />
                            </div>
                          )}
                          {course.isFeatured && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                              <Star className="h-3 w-3 text-white fill-current" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <button
                              onClick={() => navigate(`/courses/${course._id}`)}
                              className="text-sm font-semibold text-gray-900 truncate hover:text-primary-600 transition-colors text-left"
                            >
                              {course.name}
                            </button>
                            {course.isFeatured && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Featured
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                            {course.description}
                          </p>
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="h-3 w-3 mr-1.5 flex-shrink-0" />
                            <span className="line-clamp-2">
                              {formatCourseScheduleSummary(course)}
                            </span>
                            <span className="mx-2 flex-shrink-0">•</span>
                            <span className="flex-shrink-0">{formatDurationLabel(course.duration)}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="space-y-2">
                        <span className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full ${getCourseTypeColor(course.type)}`}>
                          {course.type.replace('_', ' ')}
                        </span>
                        {course.difficulty && (
                          <div>
                            <span className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full ${getDifficultyColor(course.difficulty)}`}>
                              {course.difficulty}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="space-y-1">
                        {course.price !== undefined && course.price !== null ? (
                          <>
                            <div className="text-lg font-bold text-gray-900">
                              {course.currency} {course.price.toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-500">
                              per person
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-500 italic">
                            Price varies by room type
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${
                          course.isActive ? 'bg-green-400' : 'bg-red-400'
                        }`}></div>
                        <span className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full ${
                          course.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {course.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-6 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => navigate(`/courses/${course._id}`)}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
                          title="View Course Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => handleToggleStatusClick(course)}
                          className={`p-2 rounded-lg transition-all duration-200 ${
                            course.isActive 
                              ? 'text-red-600 hover:text-red-700 hover:bg-red-50' 
                              : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                          }`}
                          title={course.isActive ? 'Deactivate Course' : 'Activate Course'}
                        >
                          {course.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </button>
                        
                        <button
                          onClick={() => handleToggleFeaturedClick(course)}
                          className={`p-2 rounded-lg transition-all duration-200 ${
                            course.isFeatured 
                              ? 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50' 
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                          }`}
                          title={course.isFeatured ? 'Remove from Featured' : 'Add to Featured'}
                        >
                          <Star className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => navigate(`/courses/${course._id}/edit`)}
                          className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200"
                          title="Edit Course"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteCourse(course._id)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                          title="Delete Course"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isTableLoading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
              Loading courses...
            </div>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white shadow-sm rounded-xl border border-gray-100 px-5 py-3">
          <p className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

        </motion.div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Course"
        message="Are you sure you want to delete this course? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="red"
        isLoading={deleting}
      />

      {/* Toggle Status Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showStatusDialog}
        onClose={() => { setShowStatusDialog(false); setCourseToToggleStatus(null) }}
        onConfirm={handleToggleStatusConfirm}
        title={courseToToggleStatus?.isActive ? 'Deactivate Course' : 'Activate Course'}
        message={courseToToggleStatus
          ? `Are you sure you want to ${courseToToggleStatus.isActive ? 'deactivate' : 'activate'} "${courseToToggleStatus.name}"?${courseToToggleStatus.isActive ? ' The course will no longer be visible to students.' : ' The course will become visible for bookings.'}`
          : ''}
        confirmText={courseToToggleStatus?.isActive ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        confirmButtonColor={courseToToggleStatus?.isActive ? 'red' : 'green'}
      />

      {/* Toggle Featured Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showFeaturedDialog}
        onClose={() => { setShowFeaturedDialog(false); setCourseToToggleFeatured(null) }}
        onConfirm={handleToggleFeaturedConfirm}
        title={courseToToggleFeatured?.isFeatured ? 'Remove from Featured' : 'Add to Featured'}
        message={courseToToggleFeatured
          ? `Are you sure you want to ${courseToToggleFeatured.isFeatured ? 'remove' : 'add'} "${courseToToggleFeatured.name}" ${courseToToggleFeatured.isFeatured ? 'from' : 'to'} featured courses?${courseToToggleFeatured.isFeatured ? ' It will no longer appear in the featured section.' : ' It will be highlighted on the homepage.'}`
          : ''}
        confirmText={courseToToggleFeatured?.isFeatured ? 'Remove' : 'Add to Featured'}
        cancelText="Cancel"
        confirmButtonColor={courseToToggleFeatured?.isFeatured ? 'blue' : 'yellow'}
      />
    </div>
  )
}

export default Courses
